import { Component, h, Host, Prop, Element, Event, EventEmitter, Watch, Method, State } from "@stencil/core";
import IMask from "imask";
import intlTelInput, {Options, CountryData} from "intl-tel-input";

const getElementAttributes = (nodeMap: NamedNodeMap): { [key: string]: string } => {
	const attributes: { [key: string]: string } = {};
	for (let i = 0; i < nodeMap.length; i++) {
		const attribute = nodeMap[i];
		if (attribute.name !== "style") {
			attributes[attribute.name] = attribute.value;
		}
	}
	return attributes;
};

type HoldCartsType = "mastercard" | "visa" | "hipercard" | "elo" | "diners" | "express";
export interface IntlTelInputProps extends Options {
	countrySearch?: boolean;
	i18n?: object | undefined;
}

@Component({
	tag: "smart-text-field",
	styleUrl: "smart-text-field.scss",
	shadow: true,
	assetsDirs: ["assets"]
})
export class SmartTextField {
	private hiddenInput: HTMLInputElement;
	private inputEl: HTMLInputElement | HTMLTextAreaElement;
	private slotInput: HTMLInputElement | HTMLTextAreaElement;
	private intlTelRef: any;

	@Element() element: HTMLSmartTextFieldElement;

	@Prop() as: "input" | "textarea" = "input";
	@Prop() label?: string;
	@Prop() message?: string;
	@Prop() disabled: boolean;
	// eslint-disable-next-line @stencil-community/strict-mutable
	@Prop({ mutable: true }) error = false;
	@Prop({ mutable: true }) value = "";
	@Prop() type: "card" | "text" | "search" | "tel" = "text";
	@Prop() inputmode: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" = "text";
	@Prop() intlTelInputOptions: IntlTelInputProps = {};
	@Prop() maskInputOptions: any = {};
  @Prop() showCheckIcon: true | false = false;

	// eslint-disable-next-line @stencil-community/strict-mutable
	@Prop({ mutable: true }) placeholder?: string;
	@Prop() name: string;

	@Prop() mask?: string;
	@Prop() unmaskedValue?: boolean = false;

	@State() cardHolder: HoldCartsType = undefined;
  @State() maskValue = this.mask;

	@Event() smartChange: EventEmitter<string>;
	@Event() smartEnterSubmit: EventEmitter<void>;
	@Event() smartCountryChange: EventEmitter<CountryData>;
	@Event() iconSearchClick: EventEmitter<string>;

	/** Sets focus on the radio button. */
	@Method()
	async focusInner() {
		if (this.inputEl !== undefined) {
			this.inputEl.focus();
		}
	}

	@Method()
	async getInputProperties() {
		return this.inputEl;
	}

	@Method()
	async getInputValue() {
		const inputValue = this.mask && this.unmaskedValue ? this.maskInstance.unmaskedValue : this.value;
		return inputValue;
	}

	@Method()
	async getIntlTelInputInstance() {
		return this.intlTelRef;
	}

  @Method()
	async updateMask(newMask: string) {
		if (this.maskInstance) {
			this.maskValue = newMask;
			this.maskInstance.updateOptions({ mask: newMask });
			this.maskInstance.updateValue();
		}
	}


	@Method()
	async getTelCountryData() {
		if(this.intlTelRef) return this.intlTelRef.selectedCountryData;
		return null;
	}

	private maskInstance = null;

	private handleKeyDown = (ev: KeyboardEvent) => {
		if(this.as === "input" && ev.key === "Enter") {
			this.smartEnterSubmit.emit();
		}
	};

	private handleChange = (event: Event) => {
		this.cardHolder = undefined;
		const inputValue = (event.target as HTMLInputElement).value;

		if(this.type === "card" && inputValue.length >= 14){
			this.handleHoldCart(inputValue.replace(/\s/g, ""));
		}

		this.value = inputValue;
		this.smartChange.emit(this.mask && this.unmaskedValue ? this.maskInstance.unmaskedValue : this.value);
	};

	private handleClick(value: string) {
		if (!this.disabled ) {
			this.iconSearchClick.emit(value);
			if (this.value) {
				this.value = "";
			}
		}
	}

	private handleHoldCart(value: string) {
		const cartHoldersValidate = {
			[/^5[1-5][0-9]{14}/.test(value).toString()]: "mastercard" as HoldCartsType,
			[/^4[0-9]{12}(?:[0-9]{3})/.test(value).toString()]: "visa" as HoldCartsType,
			[/^(606282\d{10}(\d{3})?)|(3841\d{15})/.test(value).toString()]: "hipercard" as HoldCartsType,
			[/^3(?:0[0-5]|[68][0-9])[0-9]{11}/.test(value).toString()]: "diners" as HoldCartsType,
			[/^3[47]\d{13,14}$/.test(value).toString()]: "express" as HoldCartsType,
			[/^4011(78|79)|^43(1274|8935)|^45(1416|7393|763(1|2))|^50(4175|6699|67[0-6][0-9]|677[0-8]|9[0-8][0-9]{2}|99[0-8][0-9]|999[0-9])|^627780|^63(6297|6368|6369)|^65(0(0(3([1-3]|[5-9])|4([0-9])|5[0-1])|4(0[5-9]|[1-3][0-9]|8[5-9]|9[0-9])|5([0-2][0-9]|3[0-8]|4[1-9]|[5-8][0-9]|9[0-8])|7(0[0-9]|1[0-8]|2[0-7])|9(0[1-9]|[1-6][0-9]|7[0-8]))|16(5[2-9]|[6-7][0-9])|50(0[0-9]|1[0-9]|2[1-9]|[3-4][0-9]|5[0-8]))/.test(value).toString()]: "elo" as HoldCartsType,
		};

		this.cardHolder = cartHoldersValidate["true"];
	}

	private handleChangeCountry() {
		if(!this.intlTelRef) return;
		const countryData = this.intlTelRef.getSelectedCountryData();
		this.smartCountryChange.emit(countryData);
	}

	@Watch("value")
	updateHiddenInput() {
		const inputValue = this.mask && this.unmaskedValue ? this.maskInstance.unmaskedValue : this.value;
		if(this.hiddenInput) {
			this.hiddenInput.value = inputValue;
		}
		if(this.slotInput) {
			this.slotInput.value = inputValue;
		}
	}

	private createHiddenInput() {
		this.hiddenInput = document.createElement("input");
		this.hiddenInput.type = "text";
		this.hiddenInput.name = this.name;
		this.hiddenInput.style.visibility = "hidden";
		this.hiddenInput.style.position = "absolute";
		this.hiddenInput.style.pointerEvents = "none";
		this.element.appendChild(this.hiddenInput);
	}

	private initIntlTelInput() {
		if(this.intlTelRef) return;

		const utilsScript = this.intlTelInputOptions.utilsScript ? this.intlTelInputOptions.utilsScript : "https://smartsite-production.s3.amazonaws.com/smart-web-components/vendor/intl-tel-input/js/utils.js";
		this.intlTelRef = intlTelInput(this.inputEl, {
			...this.intlTelInputOptions,
			utilsScript: process.env.NODE_ENV === "development" ? "build/assets/intl-tel-input/utils.js" : utilsScript
		});
		this.inputEl.addEventListener("countrychange", () => this.handleChangeCountry());
	}

	componentWillLoad() {
		const hasSlotInput = this.element.querySelector("[slot='input']") as HTMLInputElement | HTMLTextAreaElement;
		this.slotInput = hasSlotInput;

		if (this.name && !this.hiddenInput && !hasSlotInput) {
			this.createHiddenInput();
		}
	}

	componentDidRender() {
		if (this.type === "tel") this.initIntlTelInput();

		if (this.mask && this.as === "input") {
			if (this.maskInstance) this.maskInstance.destroy();

			this.maskInstance = IMask(this.inputEl, {
				mask: this.maskValue,
				...this.maskInputOptions,
			});

			this.maskInstance.updateValue();
		}
	}

	disconnectedCallback() {
		if(this.type === "tel") {
			if(this.inputEl) this.inputEl.removeEventListener("countrychange", () => this.handleChangeCountry());
			if(this.intlTelRef) this.intlTelRef.destroy();
		}

		if (this.maskInstance) {
			this.maskInstance.destroy();
			this.maskInstance = null;
		}
	}

	render() {
		const Tag = this.as;
		const properties = getElementAttributes(this.element.attributes);
		const idOrName = properties["id"] ? properties["id"] : properties["name"];
		const inputId: string = idOrName ? idOrName.replace(/\s/g, "") : "tid"+new Date().getTime();
		delete properties["label"];
		delete properties["message"];
		delete properties["disbaled"];
		delete properties["value"];
		delete properties["as"];
		delete properties["name"];
		delete properties["placeholder"];

		return (
			<Host part="text-field-element">
				{this.label && (
					<label
						part="label-element"
						id={`label-${inputId}`}
						htmlFor={inputId}
						class={{ disabled: this.disabled, error: this.error }}
					>
						<smart-text as="span" variant="subtitle-1">
							{this.label}
						</smart-text>
					</label>
				)}
				<div
					part="container-element"
					class='container'
				>
					<Tag
						tabindex="1"
						{...properties}
						ref={ref => this.inputEl = ref}
						placeholder={this.placeholder}
						disabled={this.disabled}
						value={this.value}
						onInput={this.handleChange}
						onKeyDown={this.handleKeyDown}
						id={inputId}
						class={{
							error: this.error,
							search: this.as === "input" && this.type === "search",
						}}
					/>
					{
						(this.as === "input" && this.type === "search") && (
							<smart-icon
								onClick={() => this.handleClick(this.value)}
								color={this.disabled ? "#9F9FA7" : "#000"}
								weight="bold"
								phosphor-icon={this.value ? "ph-x-circle" : "ph-magnifying-glass"}
								size={24}
								data-testid="smart-icon-button">
							</smart-icon>)
					}
					{
						(this.as === "input" && this.type === "card") && (
							<smart-icon
								custom-icon-name={this.cardHolder}
								size={24}
								data-testid="smart-icon-button">
							</smart-icon>)
					}
					{
						(this.as === "input" && this.type === "text" && this.showCheckIcon && (
							<smart-icon
								color={"#25A769"}
								phosphor-icon={"ph-check-circle"}
								size={24}>
							</smart-icon>
						))
					}
				</div>
				{this.message && <span
					part="message-element" class={{ disabled: this.disabled, error: this.error }}>{this.message}</span>}
				<slot name="input" />
			</Host>
		);
	}
}
